// File: frontend/src/services/websocketService.ts
import { Client, IMessage, TickerStrategy } from "@stomp/stompjs";
import { MessageEventDto } from "../models/MessageModel";
import { NotificationEventDto } from "../models/NotificationModel";

/**
 * WebSocket service for real-time messaging via STOMP protocol.
 *
 * Usage:
 *   - Call connect() after login with the JWT token
 *   - Call disconnect() on logout
 *   - Subscribe to onMessageEvent to receive real-time message events
 *   - Call sendMessage() to send messages via WebSocket
 *   - Call markAsRead() to notify read receipts via WebSocket
 *
 * Install dependency: npm install @stomp/stompjs
 */

// ─── Presence status event ────────────────────────────────────────────────────

/** Broadcast event emitted when a user changes their online presence status. */
export interface PresenceStatusEvent {
  userId: number;
  status: string;
}

// ─── Session revocation event ────────────────────────────────────────────────

/**
 * Pushed by the server when the account behind this session is closed down
 * (suspended, banned or deleted) while the user is still logged in. The token
 * in localStorage is still cryptographically valid at that point, so without
 * this event an open tab would keep looking logged in until the user happened
 * to fire a request and get a 401 back.
 *
 * reason carries the new status (SUSPENDED / BANNED / DELETED_BY_ADMIN...).
 *
 * The same queue also carries ROLE_CHANGED, which is NOT a logout: the account
 * stays signed in and only its role changed. The browser's copy of the user is
 * written at login time, so without this event the header badge, entry styling
 * and admin-panel access check would keep showing the old role until the next
 * login. `role` holds the new role for that case (and is mirrored into `reason`
 * so a client that only reads `reason` still gets a usable value).
 */
export interface SessionRevokedEvent {
  event: string;
  reason: string;
  role?: string;
}

// ─── Match update event ──────────────────────────────────────────────────────

/**
 * Broadcast by the server when an admin/moderator changes a match (score,
 * status, date, lock, referees...). Only the ID is sent: listeners re-read the
 * match through the regular public endpoint, so viewer-specific fields such as
 * entry counts stay correct.
 */
export interface MatchUpdatedEvent {
  event: string;
  matchId: number;
}

// ─── Online presence change event ────────────────────────────────────────────

/**
 * Broadcast by the server when any user goes from offline to online (first
 * WebSocket session) or from online to offline (last session). Carries no user
 * id and no count on purpose, so invisible/hidden users are not leaked:
 * listeners re-read the filtered online list through the REST endpoint.
 */
export interface OnlinePresenceChangedEvent {
  event: string;
}

const WS_BASE_URL = process.env.REACT_APP_WS_URL || "ws://localhost:8080/ws";

type MessageEventHandler = (event: MessageEventDto) => void;
type NotificationEventHandler = (event: NotificationEventDto) => void;
type ErrorHandler = (error: string) => void;
type ConnectionHandler = (connected: boolean) => void;
type PresenceStatusEventHandler = (event: PresenceStatusEvent) => void;
type SessionRevokedHandler = (event: SessionRevokedEvent) => void;
type MatchUpdatedHandler = (event: MatchUpdatedEvent) => void;
type OnlinePresenceChangedHandler = (event: OnlinePresenceChangedEvent) => void;

let stompClient: Client | null = null;
let messageEventHandlers: MessageEventHandler[] = [];
let notificationEventHandlers: NotificationEventHandler[] = [];
let errorHandlers: ErrorHandler[] = [];
let connectionHandlers: ConnectionHandler[] = [];
let presenceStatusHandlers: PresenceStatusEventHandler[] = [];
let sessionRevokedHandlers: SessionRevokedHandler[] = [];
let matchUpdatedHandlers: MatchUpdatedHandler[] = [];
let onlinePresenceChangedHandlers: OnlinePresenceChangedHandler[] = [];

/**
 * Connect to the WebSocket server with JWT authentication.
 * Should be called after successful login.
 */
export const connectWebSocket = (token: string): void => {
  if (stompClient?.active) {
    console.warn("WebSocket already connected");
    return;
  }

  stompClient = new Client({
    brokerURL: WS_BASE_URL,
    connectHeaders: {
      // Pass JWT token as a STOMP header for authentication
      Authorization: `Bearer ${token}`,
    },
    // Reconnect after 5 seconds if connection drops
    reconnectDelay: 5000,
    // Heartbeat: client sends every 10s, expects server every 10s
    // (the server now advertises 20s, so the effective server interval is 20s;
    // before the backend fix the server advertised 0 and heart-beats were off)
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 10000,
    // Run heart-beat timers in a Web Worker: background tabs throttle normal
    // timers (up to once per minute), which would let the server drop a tab
    // that is still open and make the user vanish from the online list.
    heartbeatStrategy: TickerStrategy.Worker,
    // When a heart-beat is missed, drop the dead socket immediately and let the
    // reconnect logic open a new one, instead of waiting for a close handshake
    // that never arrives on a half-open connection.
    discardWebsocketOnCommFailure: true,

    onConnect: () => {
      console.log("WebSocket connected");
      notifyConnectionHandlers(true);

      // Subscribe to personal message events queue
      stompClient?.subscribe("/user/queue/messages", (message: IMessage) => {
        try {
          const event: MessageEventDto = JSON.parse(message.body);
          notifyMessageEventHandlers(event);
        } catch (e) {
          console.error("Failed to parse WebSocket message:", e);
        }
      });

      // Subscribe to personal notification events queue
      stompClient?.subscribe("/user/queue/notifications", (message: IMessage) => {
        try {
          const event: NotificationEventDto = JSON.parse(message.body);
          notifyNotificationEventHandlers(event);
        } catch (e) {
          console.error("Failed to parse WebSocket notification:", e);
        }
      });

      // Subscribe to error queue
      stompClient?.subscribe("/user/queue/errors", (message: IMessage) => {
        try {
          const error = JSON.parse(message.body);
          notifyErrorHandlers(error.error || "Unknown WebSocket error");
        } catch (e) {
          console.error("Failed to parse WebSocket error:", e);
        }
      });

      // Subscribe to session events for this user: forced logout (SESSION_REVOKED)
      // and live role updates (ROLE_CHANGED)
      stompClient?.subscribe("/user/queue/session", (message: IMessage) => {
        try {
          const event: SessionRevokedEvent = JSON.parse(message.body);
          notifySessionRevokedHandlers(event);
        } catch (e) {
          console.error("Failed to parse session event:", e);
        }
      });

      // Subscribe to broadcast presence status changes (all clients)
      stompClient?.subscribe("/topic/online-status", (message: IMessage) => {
        try {
          const event: PresenceStatusEvent = JSON.parse(message.body);
          notifyPresenceStatusHandlers(event);
        } catch (e) {
          console.error("Failed to parse presence status event:", e);
        }
      });

      // Subscribe to broadcast online/offline transitions (data-free signal)
      stompClient?.subscribe("/topic/online-presence", (message: IMessage) => {
        try {
          const event: OnlinePresenceChangedEvent = JSON.parse(message.body);
          notifyOnlinePresenceChangedHandlers(event);
        } catch (e) {
          console.error("Failed to parse online presence event:", e);
        }
      });

      // Subscribe to broadcast match changes (score, status, lock...) from the admin panel
      stompClient?.subscribe("/topic/matches", (message: IMessage) => {
        try {
          const event: MatchUpdatedEvent = JSON.parse(message.body);
          notifyMatchUpdatedHandlers(event);
        } catch (e) {
          console.error("Failed to parse match update event:", e);
        }
      });
    },

    onDisconnect: () => {
      console.log("WebSocket disconnected");
      notifyConnectionHandlers(false);
    },

    onStompError: (frame) => {
      console.error("STOMP error:", frame.headers["message"]);
      notifyErrorHandlers(frame.headers["message"] || "WebSocket connection error");
    },

    onWebSocketError: (event) => {
      console.error("WebSocket error:", event);
    },
  });

  stompClient.activate();
};

/**
 * Disconnect from the WebSocket server.
 * Should be called on logout.
 */
export const disconnectWebSocket = (): void => {
  if (stompClient?.active) {
    stompClient.deactivate();
    console.log("WebSocket deactivated");
  }
  stompClient = null;
};

/**
 * Send a message via WebSocket STOMP.
 * Falls back to REST if WebSocket is not connected.
 *
 * @returns true if sent via WebSocket, false if WebSocket unavailable
 */
export const sendMessageWs = (
  recipientId: number,
  content: string,
  replyToMessageId?: number | null
): boolean => {
  if (!stompClient?.active) {
    return false;
  }

  try {
    stompClient.publish({
      destination: "/app/message.send",
      // replyToMessageId is only included when the user actually quoted a
      // message, so a normal send keeps sending the exact same payload as before.
      body: JSON.stringify(
        replyToMessageId
          ? { recipientId, content, replyToMessageId }
          : { recipientId, content }
      ),
    });
    return true;
  } catch (e) {
    console.warn("WebSocket publish failed, falling back to REST:", e);
    return false;
  }
};

/**
 * Notify server that messages in a conversation have been read.
 */
export const markAsReadWs = (conversationId: number): void => {
  if (!stompClient?.active) {
    return;
  }

  try {
    stompClient.publish({
      destination: "/app/message.read",
      body: JSON.stringify({ conversationId }),
    });
  } catch (e) {
    console.warn("WebSocket markAsRead failed:", e);
  }
};

/**
 * Check if WebSocket is currently connected.
 */
export const isWebSocketConnected = (): boolean => {
  return stompClient?.active ?? false;
};

// ========== Event subscription API ==========

/**
 * Subscribe to incoming message events (NEW_MESSAGE, MESSAGES_READ, UNREAD_COUNT).
 * Returns an unsubscribe function.
 */
export const onMessageEvent = (handler: MessageEventHandler): (() => void) => {
  messageEventHandlers.push(handler);
  return () => {
    messageEventHandlers = messageEventHandlers.filter((h) => h !== handler);
  };
};

/**
 * Subscribe to incoming notification events pushed from the server.
 * Returns an unsubscribe function.
 */
export const onNotificationEvent = (handler: NotificationEventHandler): (() => void) => {
  notificationEventHandlers.push(handler);
  return () => {
    notificationEventHandlers = notificationEventHandlers.filter((h) => h !== handler);
  };
};

/**
 * Subscribe to WebSocket error events.
 * Returns an unsubscribe function.
 */
export const onWebSocketError = (handler: ErrorHandler): (() => void) => {
  errorHandlers.push(handler);
  return () => {
    errorHandlers = errorHandlers.filter((h) => h !== handler);
  };
};

/**
 * Subscribe to connection state changes.
 * Returns an unsubscribe function.
 */
export const onConnectionChange = (
  handler: ConnectionHandler
): (() => void) => {
  connectionHandlers.push(handler);
  return () => {
    connectionHandlers = connectionHandlers.filter((h) => h !== handler);
  };
};

/**
 * Subscribe to presence status change events broadcast to all connected clients.
 * Returns an unsubscribe function.
 */
export const onPresenceStatusChange = (handler: PresenceStatusEventHandler): (() => void) => {
  presenceStatusHandlers.push(handler);
  return () => {
    presenceStatusHandlers = presenceStatusHandlers.filter((h) => h !== handler);
  };
};

/**
 * Subscribe to forced-logout events for the logged-in user.
 * Returns an unsubscribe function.
 */
export const onSessionRevoked = (handler: SessionRevokedHandler): (() => void) => {
  sessionRevokedHandlers.push(handler);
  return () => {
    sessionRevokedHandlers = sessionRevokedHandlers.filter((h) => h !== handler);
  };
};

/**
 * Subscribe to match change events broadcast to all connected clients.
 * Returns an unsubscribe function.
 */
export const onMatchUpdated = (handler: MatchUpdatedHandler): (() => void) => {
  matchUpdatedHandlers.push(handler);
  return () => {
    matchUpdatedHandlers = matchUpdatedHandlers.filter((h) => h !== handler);
  };
};

/**
 * Subscribe to online/offline transition signals broadcast to all connected clients.
 * The event has no payload worth reading; handlers should re-fetch their data.
 * Returns an unsubscribe function.
 */
export const onOnlinePresenceChanged = (handler: OnlinePresenceChangedHandler): (() => void) => {
  onlinePresenceChangedHandlers.push(handler);
  return () => {
    onlinePresenceChangedHandlers = onlinePresenceChangedHandlers.filter((h) => h !== handler);
  };
};

/**
 * Manually dispatch a message event to all subscribers.
 *
 * Used as a REST-to-badge bridge: when markConversationAsRead REST call
 * returns a totalUnread count, this function fires a synthetic UNREAD_COUNT
 * event so that Header.tsx updates the badge even when the WebSocket path
 * (markAsReadWs → backend → STOMP event) is unavailable or not yet connected.
 */
export const dispatchMessageEvent = (event: MessageEventDto): void => {
  notifyMessageEventHandlers(event);
};

// ========== Internal notification helpers ==========

const notifySessionRevokedHandlers = (event: SessionRevokedEvent): void => {
  sessionRevokedHandlers.forEach((handler) => {
    try {
      handler(event);
    } catch (e) {
      console.error("Error in session revoked handler:", e);
    }
  });
};

const notifyMatchUpdatedHandlers = (event: MatchUpdatedEvent): void => {
  matchUpdatedHandlers.forEach((handler) => {
    try {
      handler(event);
    } catch (e) {
      console.error("Error in match updated handler:", e);
    }
  });
};

const notifyOnlinePresenceChangedHandlers = (event: OnlinePresenceChangedEvent): void => {
  onlinePresenceChangedHandlers.forEach((handler) => {
    try {
      handler(event);
    } catch (e) {
      console.error("Error in online presence handler:", e);
    }
  });
};

const notifyMessageEventHandlers = (event: MessageEventDto): void => {
  messageEventHandlers.forEach((handler) => {
    try {
      handler(event);
    } catch (e) {
      console.error("Error in message event handler:", e);
    }
  });
};

const notifyNotificationEventHandlers = (event: NotificationEventDto): void => {
  notificationEventHandlers.forEach((handler) => {
    try {
      handler(event);
    } catch (e) {
      console.error("Error in notification event handler:", e);
    }
  });
};

const notifyErrorHandlers = (error: string): void => {
  errorHandlers.forEach((handler) => {
    try {
      handler(error);
    } catch (e) {
      console.error("Error in error handler:", e);
    }
  });
};

const notifyPresenceStatusHandlers = (event: PresenceStatusEvent): void => {
  presenceStatusHandlers.forEach((handler) => {
    try {
      handler(event);
    } catch (e) {
      console.error("Error in presence status handler:", e);
    }
  });
};

const notifyConnectionHandlers = (connected: boolean): void => {
  connectionHandlers.forEach((handler) => {
    try {
      handler(connected);
    } catch (e) {
      console.error("Error in connection handler:", e);
    }
  });
};